import React from 'react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';

export type ColumnType = {
    title: string | JSX.Element;
    items: ItemType[];
    /** Optionnel : couleur de la pastille affichée dans l'en-tête (sémantique d'état). */
    accentColor?: string;
};

export type ItemType = {
    _id: string;
    content: JSX.Element | string;
};

export const Column = ({
    column,
    selectedItemIds,
    setSelectedItemIds,
    columnWidth = '220px',
}: {
    column: ColumnType;
    selectedItemIds?: string[];
    setSelectedItemIds?: (value: React.SetStateAction<string[]>) => void;
    columnWidth?: string;
}) => {
    const { title, items, accentColor } = column;
    const columnItemIds = items.map(({ _id }) => _id);

    const selectable = !!selectedItemIds && !!setSelectedItemIds;
    const selectedCount = selectedItemIds ? columnItemIds.filter((id) => selectedItemIds.includes(id)).length : 0;
    const allSelected = !!columnItemIds.length && selectedCount === columnItemIds.length;
    const checkboxState: boolean | 'indeterminate' = allSelected ? true : selectedCount > 0 ? 'indeterminate' : false;

    function toggleColumnSelection() {
        if (!selectedItemIds || !setSelectedItemIds) {
            return;
        }
        const notSelected = columnItemIds.filter((id) => !selectedItemIds.includes(id));
        if (!notSelected.length) {
            setSelectedItemIds(selectedItemIds.filter((id) => !columnItemIds.includes(id)));
        } else {
            setSelectedItemIds([...selectedItemIds, ...notSelected]);
        }
    }

    return (
        <div className="flex h-full flex-col overflow-hidden rounded-xl bg-muted/40" style={{ width: columnWidth }}>
            <div className="flex items-center gap-2 border-b border-border/50 px-3 py-2.5">
                {selectable ? (
                    <Checkbox
                        checked={checkboxState}
                        onCheckedChange={toggleColumnSelection}
                        disabled={!columnItemIds.length}
                        aria-label="Sélectionner la colonne"
                    />
                ) : null}
                {accentColor ? (
                    <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: accentColor }} />
                ) : null}
                <span className="truncate text-sm font-semibold text-foreground">{title}</span>
                <Badge variant="secondary" className="ml-auto tabular-nums">
                    {items.length}
                </Badge>
            </div>
            <ScrollArea className="min-h-0 flex-1">
                <div className="flex flex-col gap-2 p-2">
                    {items.length ? (
                        items.map(({ _id, content }) => <div key={_id}>{content}</div>)
                    ) : (
                        <div className="flex h-20 items-center justify-center rounded-lg border border-dashed border-border/70 text-xs text-muted-foreground">
                            Aucun élément
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
};
